import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { getBallot, saveBallot, submitBallotApi, type CandidateHappening } from '@/lib/selections-api';
import { playableSource, RowPlay, PlayerBar } from './BallotPlayer';
import type { BallotEntryInput, AllocateRoundConfig } from '@littlebooker/dal';

interface AllocateBallotProps {
  selectionId: string;
  roundId: string;
  config: AllocateRoundConfig;
  /** Effective shortlist happening ids (round snapshot, or revealed-round fallback). */
  shortlistIds: string[];
  candidates: CandidateHappening[];
  recusedIds: Set<string>;
  editable: boolean;
}

const numInput = 'w-20 rounded-md border border-input bg-background px-2 py-1';
const noteInput = 'w-full rounded-md border border-input bg-background px-2 py-1 text-sm';

export function AllocateBallot({
  selectionId,
  roundId,
  config,
  shortlistIds,
  candidates,
  recusedIds,
  editable,
}: AllocateBallotProps) {
  const queryClient = useQueryClient();
  const budget = config.budget ?? 100;
  const cap = config.per_item_cap ?? budget;
  const requireNoteAbove =
    typeof config.require_note_above === 'number' ? config.require_note_above : null;

  const shortlist = useMemo(
    () =>
      shortlistIds
        .map((id) => candidates.find((c) => c.id === id))
        .filter((c): c is CandidateHappening => !!c),
    [shortlistIds, candidates],
  );

  const { data: ballot, isLoading } = useQuery({
    queryKey: ['selection-ballot', roundId],
    queryFn: () => getBallot(selectionId, roundId),
  });

  const [points, setPoints] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [nowPlaying, setNowPlaying] = useState<{ id: string; url: string; title: string } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (!ballot) return;
    const p: Record<string, number> = {};
    const n: Record<string, string> = {};
    for (const e of ballot.entries) {
      p[e.happening_id] = e.points ?? 0;
      if (e.note) n[e.happening_id] = e.note;
    }
    setPoints(p);
    setNotes(n);
  }, [ballot?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start collapsed if already submitted (first load only).
  useEffect(() => {
    if (!initRef.current && ballot) {
      initRef.current = true;
      if (ballot.status === 'submitted') setCollapsed(true);
    }
  }, [ballot]);

  const total = Object.values(points).reduce((s, v) => s + (v || 0), 0);
  const overCap = shortlist.some((c) => (points[c.id] || 0) > cap);
  const missingNote =
    requireNoteAbove != null &&
    shortlist.some((c) => (points[c.id] || 0) > requireNoteAbove && !(notes[c.id] || '').trim());
  const balanced = total === budget;
  const canSubmit = editable && balanced && !overCap && !missingNote;

  const toEntries = (): BallotEntryInput[] =>
    shortlist
      .filter((c) => !recusedIds.has(c.id) && (points[c.id] || 0) > 0)
      .map((c) => ({
        happening_id: c.id,
        points: points[c.id] || 0,
        is_wildcard: false,
        note: (notes[c.id] || '').trim() || null,
      }));

  const saveMutation = useMutation({
    mutationFn: () => saveBallot(selectionId, roundId, toEntries()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['selection-ballot', roundId] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to save'),
  });
  const submitMutation = useMutation({
    mutationFn: () => submitBallotApi(selectionId, roundId, toEntries()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selection-ballot', roundId] });
      setCollapsed(true); // collapse the list once submitted
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to submit'),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading ballot…</p>;
  if (shortlist.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No shortlist yet — reveal the nominate round first.
      </p>
    );
  }

  const remaining = budget - total;
  const submitted = ballot?.status === 'submitted';
  const allocatedCount = shortlist.filter((c) => (points[c.id] || 0) > 0).length;

  if (collapsed) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 p-3 text-sm">
        <span className="font-medium text-primary">✓ Ballot submitted</span>
        <span className="text-muted-foreground">
          {total}/{budget} points across {allocatedCount} show{allocatedCount === 1 ? '' : 's'}
        </span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setCollapsed(false)}>
          View / edit
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span>
          Total{' '}
          <span className={balanced ? 'font-medium text-foreground' : 'font-medium text-destructive'}>
            {total}
          </span>{' '}
          / {budget}
        </span>
        <span className={remaining === 0 ? 'text-muted-foreground' : 'text-destructive'}>
          {remaining > 0 ? `${remaining} left to spend` : remaining < 0 ? `${-remaining} over budget` : 'balanced'}
        </span>
        <span className="text-muted-foreground">
          {submitted ? 'Submitted' : 'Draft'}
          {!editable && ' · round not open'}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">
        Spend exactly {budget} points across the shortlist (max {cap} per show
        {requireNoteAbove != null ? `; note required above ${requireNoteAbove}` : ''}). Spread reflects
        intensity — shares are normalized at tally.
      </p>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Show</TableHead>
              <TableHead>Artists</TableHead>
              <TableHead className="w-24">Points</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shortlist.map((c) => {
              const recused = recusedIds.has(c.id);
              const pts = points[c.id] || 0;
              const over = pts > cap;
              const needNote = requireNoteAbove != null && pts > requireNoteAbove && !(notes[c.id] || '').trim();
              return (
                <Fragment key={c.id}>
                <TableRow className={recused ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <RowPlay
                        source={playableSource(c.archive_links)}
                        onPlay={() => {
                          const s = playableSource(c.archive_links);
                          if (s) setNowPlaying({ id: c.id, url: s.url, title: c.title });
                        }}
                      />
                      {c.episode_number != null && (
                        <span className="text-muted-foreground">#{c.episode_number}</span>
                      )}
                      <span>{c.title}</span>
                      {recused && <span className="text-xs text-muted-foreground">(recused)</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.artists.map((a) => a.name).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <NumberInput
                      min={0}
                      max={cap}
                      allowEmpty={false}
                      className={over ? `${numInput} border-destructive` : numInput}
                      value={recused ? 0 : pts}
                      disabled={!editable || recused}
                      onChange={(n) => setPoints((prev) => ({ ...prev, [c.id]: n ?? 0 }))}
                    />
                  </TableCell>
                  <TableCell>
                    {!recused && pts > 0 && (
                      <input
                        className={needNote ? `${noteInput} border-destructive` : noteInput}
                        placeholder={
                          requireNoteAbove != null && pts > requireNoteAbove
                            ? 'Note required'
                            : 'Optional note'
                        }
                        value={notes[c.id] || ''}
                        disabled={!editable}
                        onChange={(e) => setNotes((prev) => ({ ...prev, [c.id]: e.target.value }))}
                      />
                    )}
                  </TableCell>
                </TableRow>
                {nowPlaying?.id === c.id && (
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={4}>
                      <PlayerBar track={nowPlaying} onClose={() => setNowPlaying(null)} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {(overCap || missingNote) && (
        <p className="text-sm text-destructive">
          {overCap && `Some allocations exceed the ${cap}-point cap. `}
          {missingNote && `Notes required above ${requireNoteAbove} points. `}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {editable && !submitted && (
        <p className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          ⚠ Not submitted yet — “Save draft” only saves your progress. Click{' '}
          <span className="font-medium">Submit</span> to actually cast your vote.
        </p>
      )}

      {editable && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError('');
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setError('');
              submitMutation.mutate();
            }}
            disabled={!canSubmit || submitMutation.isPending}
          >
            {submitMutation.isPending ? 'Submitting…' : submitted ? 'Re-submit' : 'Submit'}
          </Button>
        </div>
      )}
    </div>
  );
}
